import React from "react";
import { Dialog } from "../../../ui/components/Dialog";
import { Button } from "../../../ui/components/Button";
import { FeatherAlertCircle } from "@subframe/core";

export interface ClosedMonthConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when user clicks "Submit for Review". Parent should close this modal and open ReviewRequestModal. */
  onConfirm: () => void;
  /** Optional entity label (e.g. "Invoice", "Bill") for context. */
  entityLabel?: string;
  /** Optional custom message. */
  message?: string;
  /** Whether the confirm action is in progress (e.g. opening review flow). */
  loading?: boolean;
}

const defaultMessage =
  "This document is in a closed month. You cannot edit it directly. Submit your changes for review and approval.";

/**
 * Modal shown when a non-Admin/CFO user attempts to edit or create in a closed month.
 * Explains that changes require review and offers "Submit for Review" (parent opens ReviewRequestModal) or Cancel.
 */
export const ClosedMonthConfirmModal: React.FC<
  ClosedMonthConfirmModalProps
> = ({
  open,
  onOpenChange,
  onConfirm,
  entityLabel,
  message,
  loading = false,
}) => {
  const displayMessage = message
    ? message
    : entityLabel
    ? `This ${entityLabel} is in a closed month. You cannot edit it directly. Submit your changes for review and approval.`
    : defaultMessage;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[420px] max-w-[520px]">
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          {/* Header with icon */}
          <div className="flex w-full items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-100">
              <FeatherAlertCircle className="h-5 w-5 text-warning-600" />
            </div>
            <span className="text-heading-2 font-heading-2 text-default-font">
              Closed month
            </span>
          </div>

          {/* Message in a subtle info block */}
          <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-4">
            <span className="text-body font-body text-neutral-700 leading-relaxed">
              {displayMessage}
            </span>
          </div>

          {/* Actions */}
          <div className="flex w-full items-center justify-end gap-3 pt-1">
            <Button
              variant="neutral-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleConfirm}
              loading={loading}
              disabled={loading}
            >
              Submit for Review
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
